import { useEffect, useRef, useState } from "react";
import { Download, ImagePlus, Loader2, Send, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, setCurrency } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const EMPTY = {
  name: "",
  company: "",
  vat: "",
  address: "",
  phone: "",
  iban: "",
  currency: "EUR",
  default_tax_rate: 22,
  tax_label: "",
  auto_reminders: false,
  reminder_days: 30,
  tax_code: "",
  fiscal_regime: "",
  street: "",
  zip: "",
  city: "",
  province: "",
  country: "IT",
  default_cassa_rate: 0,
  default_withholding_rate: 0,
  default_stamp_duty: 0,
  cassa_type: "TC22",
  withholding_type: "RT01",
  accountant_email: "",
  accountant_auto: false,
  accountant_day: 5,
};

// Campi richiesti dalla fattura elettronica italiana (FatturaPA).
const SDI_FIELDS = [
  ["tax_code", "Codice fiscale"],
  ["street", "Indirizzo (via e numero)"],
  ["zip", "CAP"],
  ["city", "Comune"],
  ["province", "Provincia (2 lettere)"],
];
const REGIMES = [
  ["RF01", "RF01 — Ordinario"],
  ["RF19", "RF19 — Forfettario"],
  ["RF02", "RF02 — Contribuenti minimi"],
  ["RF04", "RF04 — Agricoltura e pesca"],
  ["RF05", "RF05 — Vendita sali e tabacchi"],
  ["RF11", "RF11 — Agenzie viaggi e turismo"],
  ["RF16", "RF16 — IVA per cassa P.A."],
  ["RF17", "RF17 — IVA per cassa"],
];

const CURRENCIES = ["EUR", "USD", "GBP", "CHF", "CAD", "AUD", "SEK", "NOK", "DKK", "PLN"];

const Impostazioni = () => {
  const { setUser } = useAuth();
  const { t, lang } = useI18n();
  const [form, setForm] = useState(EMPTY);
  const [logo, setLogo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [fiscalOptions, setFiscalOptions] = useState({ casse: [], withholdings: [] });
  const [importing, setImporting] = useState(false);
  const [sending, setSending] = useState(false);

  const sendToAccountant = async () => {
    setSending(true);
    try {
      await api.put("/auth/profile", form);
      const { data } = await api.post("/accountant/send", {});
      toast.success(
        t("acc.sent").replace("{n}", data.documents).replace("{m}", data.month)
      );
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };
  const importRef = useRef(null);

  const importXml = async (file) => {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    setImporting(true);
    try {
      const { data } = await api.post("/import/xml", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        t("imp.done").replace("{n}", data.imported).replace("{c}", data.clients_added)
      );
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setImporting(false);
      if (importRef.current) importRef.current.value = "";
    }
  };
  const fileRef = useRef(null);

  const FIELDS = [
    ["name", t("register.name")],
    ["company", t("register.company")],
    ["vat", t("settings.vat")],
    ["address", t("common.address")],
    ["phone", t("common.phone")],
    ["iban", t("settings.iban")],
  ];

  const loadLogo = async () => {
    try {
      const res = await api.get("/profile/logo", { responseType: "blob" });
      setLogo(res.data?.size ? URL.createObjectURL(res.data) : null);
    } catch {
      setLogo(null);
    }
  };

  const loaded = useRef(false);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => {
      if (loaded.current) return;
      loaded.current = true;
      setForm({
        name: data.name || "",
        company: data.company || "",
        vat: data.vat || "",
        address: data.address || "",
        phone: data.phone || "",
        iban: data.iban || "",
        currency: data.currency || "EUR",
        default_tax_rate: data.default_tax_rate ?? 22,
        tax_label: data.tax_label || "",
        auto_reminders: !!data.auto_reminders,
        reminder_days: data.reminder_days || 30,
        tax_code: data.tax_code || "",
        fiscal_regime: data.fiscal_regime || "",
        street: data.street || "",
        zip: data.zip || "",
        city: data.city || "",
        province: data.province || "",
        country: data.country || "IT",
        default_cassa_rate: data.default_cassa_rate ?? 0,
        default_withholding_rate: data.default_withholding_rate ?? 0,
        default_stamp_duty: data.default_stamp_duty ?? 0,
        cassa_type: data.cassa_type || "TC22",
        withholding_type: data.withholding_type || "RT01",
        accountant_email: data.accountant_email || "",
        accountant_auto: !!data.accountant_auto,
        accountant_day: data.accountant_day || 5,
      });
    });
    loadLogo();
    api.get("/sdi/fiscal-options").then(({ data }) => setFiscalOptions(data)).catch(() => {});
  }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    try {
      await api.put("/auth/profile", form);
      const { data } = await api.get("/auth/me");
      setUser(data);
      setCurrency(data.currency || "EUR");
      toast.success(t("settings.saved"));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    setUploading(true);
    try {
      await api.post("/profile/logo", body, { headers: { "Content-Type": "multipart/form-data" } });
      await loadLogo();
      toast.success(t("logo.uploaded"));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = async () => {
    await api.delete("/profile/logo");
    setLogo(null);
  };

  return (
    <div className="max-w-xl space-y-5" data-testid="settings-page">
      <div className="card-flat p-6">
        <h2 className="text-base font-semibold md:text-lg">{t("settings.language")}</h2>
        <div className="mt-3">
          <LanguageSwitcher />
        </div>
      </div>

      {lang === "it" && (
      <div className="card-flat p-6" data-testid="sdi-card">
        <h2 className="text-base font-semibold md:text-lg">Fattura elettronica (Italia)</h2>
        <p className="mt-1 text-sm text-slate-500">
          Compila questi dati e da ogni fattura potrai scaricare il file XML da caricare sul
          portale Agenzia delle Entrate. L'invio automatico allo SDI arriverà a breve.
        </p>
        <div className="mt-4">
          <Label htmlFor="fiscal-regime">Regime fiscale</Label>
          <select
            id="fiscal-regime"
            data-testid="profile-fiscal-regime"
            value={form.fiscal_regime}
            onChange={(e) => setForm({ ...form, fiscal_regime: e.target.value })}
            className="mt-1.5 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm focus:ring-2 focus:ring-blue-600"
          >
            <option value="">Seleziona…</option>
            {REGIMES.map(([code, label]) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {SDI_FIELDS.map(([key, label]) => (
            <div key={key}>
              <Label htmlFor={`sdi-${key}`}>{label}</Label>
              <Input
                id={`sdi-${key}`}
                data-testid={`profile-${key}`}
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                className="mt-1.5 focus:ring-2 focus:ring-blue-600"
              />
            </div>
          ))}
        </div>
        <Button
          data-testid="save-sdi"
          onClick={save}
          className="mt-5 bg-blue-600 text-white hover:bg-blue-700"
        >
          {t("common.save")}
        </Button>
      </div>
      )}

      {lang === "it" && (
      <div className="card-flat p-6" data-testid="fiscal-settings-card">
        <h2 className="text-base font-semibold md:text-lg">{t("settings.fiscalTitle")}</h2>
        <p className="mt-1 text-sm text-slate-500">{t("settings.fiscalHint")}</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <div>
            <Label htmlFor="default-cassa">{t("settings.defaultCassa")}</Label>
            <Input
              id="default-cassa"
              data-testid="profile-default-cassa"
              type="number"
              step="0.5"
              value={form.default_cassa_rate}
              onChange={(e) =>
                setForm({ ...form, default_cassa_rate: Number(e.target.value) || 0 })
              }
              className="font-mono-num mt-1.5 focus:ring-2 focus:ring-blue-600"
            />
          </div>
          <div>
            <Label htmlFor="default-withholding">{t("settings.defaultWithholding")}</Label>
            <Input
              id="default-withholding"
              data-testid="profile-default-withholding"
              type="number"
              step="1"
              value={form.default_withholding_rate}
              onChange={(e) =>
                setForm({ ...form, default_withholding_rate: Number(e.target.value) || 0 })
              }
              className="font-mono-num mt-1.5 focus:ring-2 focus:ring-blue-600"
            />
          </div>
          <div>
            <Label htmlFor="default-stamp">{t("settings.defaultStamp")}</Label>
            <Input
              id="default-stamp"
              data-testid="profile-default-stamp"
              type="number"
              step="0.01"
              value={form.default_stamp_duty}
              onChange={(e) =>
                setForm({ ...form, default_stamp_duty: Number(e.target.value) || 0 })
              }
              className="font-mono-num mt-1.5 focus:ring-2 focus:ring-blue-600"
            />
          </div>
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="cassa-type">{t("settings.cassaType")}</Label>
            <select
              id="cassa-type"
              data-testid="profile-cassa-type"
              value={form.cassa_type}
              onChange={(e) => setForm({ ...form, cassa_type: e.target.value })}
              className="mt-1.5 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm focus:ring-2 focus:ring-blue-600"
            >
              {fiscalOptions.casse.map(({ code, label }) => (
                <option key={code} value={code}>{`${code} — ${label}`}</option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="withholding-type">{t("settings.withholdingType")}</Label>
            <select
              id="withholding-type"
              data-testid="profile-withholding-type"
              value={form.withholding_type}
              onChange={(e) => setForm({ ...form, withholding_type: e.target.value })}
              className="mt-1.5 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm focus:ring-2 focus:ring-blue-600"
            >
              {fiscalOptions.withholdings.map(({ code, label }) => (
                <option key={code} value={code}>{`${code} — ${label}`}</option>
              ))}
            </select>
          </div>
        </div>
        <Button
          data-testid="save-fiscal"
          onClick={save}
          className="mt-5 bg-blue-600 text-white hover:bg-blue-700"
        >
          {t("common.save")}
        </Button>
      </div>
      )}

      {lang === "it" && (
      <div className="card-flat p-6" data-testid="import-card">
        <h2 className="text-base font-semibold md:text-lg">{t("imp.title")}</h2>
        <p className="mt-1 text-sm text-slate-500">{t("imp.hint")}</p>
        <input
          ref={importRef}
          type="file"
          accept=".xml,.zip"
          className="hidden"
          onChange={(e) => importXml(e.target.files?.[0])}
        />
        <Button
          data-testid="import-xml-button"
          variant="outline"
          disabled={importing}
          onClick={() => importRef.current?.click()}
          className="mt-4 gap-2"
        >
          {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {t("imp.choose")}
        </Button>
      </div>
      )}

      <div className="card-flat p-6" data-testid="accountant-card">
        <h2 className="text-base font-semibold md:text-lg">{t("acc.title")}</h2>
        <p className="mt-1 text-sm text-slate-500">{t("acc.hint")}</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-[1fr_120px]">
          <div>
            <Label htmlFor="accountant-email">{t("acc.email")}</Label>
            <Input
              id="accountant-email"
              data-testid="profile-accountant-email"
              type="email"
              placeholder="studio@commercialista.it"
              value={form.accountant_email}
              onChange={(e) => setForm({ ...form, accountant_email: e.target.value })}
              className="mt-1.5 focus:ring-2 focus:ring-blue-600"
            />
          </div>
          <div>
            <Label htmlFor="accountant-day">{t("acc.day")}</Label>
            <Input
              id="accountant-day"
              data-testid="profile-accountant-day"
              type="number"
              min="1"
              max="28"
              value={form.accountant_day}
              onChange={(e) =>
                setForm({ ...form, accountant_day: Number(e.target.value) || 5 })
              }
              className="font-mono-num mt-1.5 focus:ring-2 focus:ring-blue-600"
            />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between rounded-lg bg-slate-50 p-3">
          <Label htmlFor="accountant-auto" className="text-sm">{t("acc.auto")}</Label>
          <Switch
            id="accountant-auto"
            data-testid="profile-accountant-auto"
            checked={form.accountant_auto}
            onCheckedChange={(v) => setForm({ ...form, accountant_auto: v })}
          />
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          <Button
            data-testid="save-accountant"
            onClick={save}
            className="bg-blue-600 text-white hover:bg-blue-700"
          >
            {t("common.save")}
          </Button>
          <Button
            data-testid="send-accountant-now"
            variant="outline"
            disabled={sending || !form.accountant_email}
            onClick={sendToAccountant}
            className="gap-2"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {t("acc.sendNow")}
          </Button>
        </div>
      </div>

      <div className="card-flat p-6" data-testid="currency-card">
        <h2 className="text-base font-semibold md:text-lg">{t("settings.intlTitle")}</h2>
        <p className="mt-1 text-sm text-slate-500">{t("settings.intlHint")}</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="currency">{t("settings.currency")}</Label>
            <select
              id="currency"
              data-testid="profile-currency"
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
              className="mt-1.5 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm focus:ring-2 focus:ring-blue-600"
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="default-tax">{t("settings.defaultTax")}</Label>
            <Input
              id="default-tax"
              data-testid="profile-default-tax"
              type="number"
              step="0.5"
              value={form.default_tax_rate}
              onChange={(e) =>
                setForm({ ...form, default_tax_rate: Number(e.target.value) || 0 })
              }
              className="font-mono-num mt-1.5 focus:ring-2 focus:ring-blue-600"
            />
          </div>
        </div>
        <div className="mt-4">
          <Label htmlFor="tax-label">{t("settings.taxLabel")}</Label>
          <Input
            id="tax-label"
            data-testid="profile-tax-label"
            value={form.tax_label}
            onChange={(e) => setForm({ ...form, tax_label: e.target.value })}
            placeholder="VAT"
            className="mt-1.5 focus:ring-2 focus:ring-blue-600"
          />
          <p className="mt-1.5 text-xs text-slate-500">{t("settings.taxLabelHint")}</p>
        </div>
        <Button
          data-testid="save-currency"
          type="button"
          onClick={save}
          className="mt-5 bg-blue-600 text-white hover:bg-blue-700"
        >
          {t("common.save")}
        </Button>
      </div>

      <div className="card-flat p-6">
        <h2 className="text-base font-semibold md:text-lg">{t("logo.title")}</h2>
        <p className="mt-1 text-sm text-slate-500">{t("logo.hint")}</p>
        <div className="mt-4 flex items-center gap-4">
          <div className="flex h-20 w-32 items-center justify-center rounded-md border border-dashed border-slate-300 bg-slate-50">
            {logo ? (
              <img src={logo} alt="logo" data-testid="logo-preview" className="max-h-16 max-w-28 object-contain" />
            ) : (
              <ImagePlus className="h-5 w-5 text-slate-400" />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              data-testid="logo-input"
              onChange={(e) => uploadLogo(e.target.files?.[0])}
              className="hidden"
            />
            <Button
              data-testid="logo-upload"
              type="button"
              variant="outline"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="gap-2"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
              {t("logo.upload")}
            </Button>
            {logo && (
              <Button
                data-testid="logo-remove"
                type="button"
                variant="ghost"
                onClick={removeLogo}
                className="gap-2 text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" /> {t("logo.remove")}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="card-flat p-6">
        <h2 className="text-base font-semibold md:text-lg">{t("settings.autoTitle")}</h2>
        <p className="mt-1 text-sm text-slate-500">{t("settings.autoHint")}</p>
        <div className="mt-4 flex items-center justify-between rounded-md border border-slate-200 px-4 py-3">
          <Label htmlFor="auto-reminders" className="text-sm">
            {t("settings.autoToggle")}
          </Label>
          <Switch
            id="auto-reminders"
            data-testid="auto-reminders-switch"
            checked={form.auto_reminders}
            onCheckedChange={(v) => setForm({ ...form, auto_reminders: v })}
          />
        </div>
        <div className="mt-3">
          <Label htmlFor="reminder-days">{t("settings.reminderDays")}</Label>
          <Input
            id="reminder-days"
            data-testid="reminder-days"
            type="number"
            value={form.reminder_days}
            onChange={(e) => setForm({ ...form, reminder_days: Number(e.target.value) || 0 })}
            className="font-mono-num mt-1.5 focus:ring-2 focus:ring-blue-600"
          />
        </div>
      </div>

      <div className="card-flat p-6">
        <h2 className="text-base font-semibold md:text-lg">{t("settings.title")}</h2>
        <p className="mt-1 text-sm text-slate-500">{t("settings.subtitle")}</p>
        <form onSubmit={save} className="mt-6 space-y-4">
          {FIELDS.map(([key, label]) => (
            <div key={key}>
              <Label htmlFor={key}>{label}</Label>
              <Input
                id={key}
                data-testid={`profile-${key}`}
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                className="mt-1.5 focus:ring-2 focus:ring-blue-600"
              />
            </div>
          ))}
          <Button data-testid="save-profile" type="submit" className="bg-blue-600 text-white hover:bg-blue-700">
            {t("common.save")}
          </Button>
        </form>
      </div>

      <div className="card-flat p-6" data-testid="install-card">
        <h2 className="text-base font-semibold md:text-lg">{t("pwa.title")}</h2>
        <p className="mt-1 text-sm text-slate-500">{t("pwa.settingsHint")}</p>
        <Button
          data-testid="recall-install-prompt"
          variant="outline"
          className="mt-4 gap-2"
          onClick={() => {
            localStorage.removeItem("operativo-install-dismissed");
            window.location.reload();
          }}
        >
          <Download className="h-4 w-4" /> {t("pwa.cta")}
        </Button>
      </div>
    </div>
  );
};

export default Impostazioni;
